import { create } from 'zustand';
import type { Channel } from '../types';
import * as api from '../lib/api';
import { resolveDefaultChannel } from '../lib/defaultChannel';
import { useAuthStore } from './useAuthStore';

interface ChannelState {
  channels: Channel[];
  currentChannelId: string | null;
  channelMembersVersion: Record<string, number>;

  setChannels: (channels: Channel[]) => void;
  addChannel: (channel: Channel) => void;
  removeChannel: (channelId: string) => void;
  updateChannel: (channelId: string, updates: Partial<Channel>) => void;
  setCurrentChannel: (channelId: string | null) => void;
  updateUnread: (channelId: string, count: number) => void;
  clearUnread: (channelId: string) => void;
  bumpChannelMembersVersion: (channelId: string) => void;
  navigateAfterLeave: (channelId: string) => void;
  purgeSubtree: (channelIds: string[]) => void;
  reset: () => void;

  loadChannels: (guildId?: string) => Promise<void>;
  selectChannel: (channelId: string) => void;
  createChannel: (
    name: string,
    topic?: string,
    memberIds?: string[],
    visibility?: 'public' | 'private',
  ) => Promise<Channel>;
}

/** Where the member ends up after the channel they were reading disappeared. */
const replacementChannelId = (channels: Channel[]): string | null =>
  resolveDefaultChannel(channels, useAuthStore.getState().guildId, 'main')?.id ?? null;

let channelsGeneration = 0;

export const useChannelStore = create<ChannelState>()((set, get) => ({
  channels: [],
  currentChannelId: null,
  channelMembersVersion: {},

  setChannels: (channels) => set({ channels }),

  addChannel: (channel) =>
    set((s) => {
      if (!channel || !channel.id) return s;
      if (s.channels.some((c) => c.id === channel.id)) return s;
      return { channels: [...s.channels, channel] };
    }),

  removeChannel: (channelId) =>
    set((s) => {
      const channels = s.channels.filter((c) => c.id !== channelId);
      if (s.currentChannelId !== channelId) return { channels };
      return { channels, currentChannelId: replacementChannelId(channels) };
    }),

  updateChannel: (channelId, updates) =>
    set((s) => ({
      channels: s.channels.map((c) => (c.id === channelId ? { ...c, ...updates } : c)),
    })),

  setCurrentChannel: (channelId) => set({ currentChannelId: channelId }),

  updateUnread: (channelId, count) =>
    set((s) => ({
      channels: s.channels.map((c) => (c.id === channelId ? { ...c, unread_count: count } : c)),
    })),

  clearUnread: (channelId) =>
    set((s) => ({
      channels: s.channels.map((c) => (c.id === channelId ? { ...c, unread_count: 0 } : c)),
    })),

  bumpChannelMembersVersion: (channelId) =>
    set((s) => {
      const v = { ...s.channelMembersVersion };
      v[channelId] = (v[channelId] ?? 0) + 1;
      return { channelMembersVersion: v };
    }),

  navigateAfterLeave: (channelId) =>
    set((s) => {
      const channels = s.channels.filter((c) => c.id !== channelId);
      const channelMembersVersion = { ...s.channelMembersVersion };
      delete channelMembersVersion[channelId];
      return { channels, currentChannelId: replacementChannelId(channels), channelMembersVersion };
    }),

  purgeSubtree: (channelIds) => {
    const removed = new Set(channelIds);
    channelsGeneration++;
    set((s) => {
      const channels = s.channels.filter((channel) => !removed.has(channel.id));
      const channelMembersVersion = { ...s.channelMembersVersion };
      for (const id of removed) delete channelMembersVersion[id];
      return {
        channels,
        channelMembersVersion,
        currentChannelId:
          s.currentChannelId && removed.has(s.currentChannelId)
            ? replacementChannelId(channels)
            : s.currentChannelId,
      };
    });
  },

  loadChannels: async (guildId) => {
    const targetGuildId = guildId ?? useAuthStore.getState().guildId;
    const generation = ++channelsGeneration;
    const response = targetGuildId
      ? await api.fetchGuildChannels(targetGuildId)
      : await api.fetchChannels();
    if (generation !== channelsGeneration) return;
    if (targetGuildId && useAuthStore.getState().guildId !== targetGuildId) return;
    set({ channels: response.channels });
  },

  selectChannel: (channelId) => {
    set({ currentChannelId: channelId });
    get().clearUnread(channelId);
    api.markChannelRead(channelId).catch(() => {});
  },

  createChannel: async (name, topic, memberIds, visibility) => {
    const guildId = useAuthStore.getState().guildId;
    if (!guildId) throw new Error('Select a Project before creating a channel');
    const channel = await api.createChannel(guildId, name, topic, memberIds, visibility);
    get().addChannel(channel);
    await useAuthStore.getState().loadPermissions();
    return channel;
  },

  reset: () => {
    channelsGeneration++;
    set({ channels: [], currentChannelId: null, channelMembersVersion: {} });
  },
}));
